


























import { computed, defineComponent, reactive, ref, toRefs, watch } from '@vue/composition-api';
import Sidebar from '@/components/Sidebar.vue';
import InputGroup from '@/components/InputGroup.vue';
import Button from '@/components/Button.vue';
import useSaveButton from '@/composables/buttons/useSaveButton';
import useCancelButton from '@/composables/buttons/useCancelButton';
import { useGetAuthoritiesQuery, useGetAuthorityQuery } from '@/graphql/types';
import { useResult } from '@vue/apollo-composable';
import useVuelidate from '@vuelidate/core';
import { required, withI18nMessage, maxLength } from '@/utilities/validators';
import Select2 from '@/components/Select2.vue';
import useCreateAuthorityMutation from './composables/useCreateAuthorityMutation';
import useUpdateAuthorityMutation from './composables/useUpdateAuthorityMutation';
import useDeleteAuthorityMutation from './composables/useDeleteAuthorityMutation';
import IMenu from '@/interfaces/IMenu';
import Dropdown from '@/components/Dropdown.vue';
import useDefaultStatusS2Selections from '@/composables/useDefaultStatusS2Selections';
import { S2Selection } from '@/types/s2selection';
import { FilterState } from '@/types/enums';

export default defineComponent({
  props: {
    id: {
      type: String,
      required: false
    }
  },
  components: { Sidebar, InputGroup, Button, Select2, Dropdown },
  setup(props, { root }) {
    const isNew = ref(props.id == null);
    const authority = reactive({
      id: '',
      name: '',
      cvr: '',
      isActive: true,
      createdAt: null
    });

    const status = ref(null as S2Selection<FilterState> | null);
    const statusList = useDefaultStatusS2Selections().filter(x => x.object !== FilterState.All);

    const hasChanges = ref<boolean | null>(null);
    if (isNew.value) hasChanges.value = false;

    const authoritiesQuery = useGetAuthoritiesQuery({
      fetchPolicy: 'network-only'
    });

    const authorities = computed(() => authoritiesQuery.result.value?.authorities ?? []);

    const { result, loading, error } = useGetAuthorityQuery(
      () => ({ id: props.id }),
      () => ({
        enabled: !isNew.value,
        fetchPolicy: 'network-only'
      })
    );

    const v$ = useVuelidate(
      {
        ...useValidations(uniqueNameValidator, uniqueCvrValidator, legitCvrValidator)
      },
      { ...toRefs(authority) },
      { $autoDirty: true }
    );

    function uniqueNameValidator() {
      if (authority.name == '') return true;

      if (
        authorities.value?.some(
          element => element?.name?.toLowerCase() === authority.name?.toLowerCase() && element?.id != authority.id
        )
      )
        return false;
      return true;
    }

    function uniqueCvrValidator() {
      if (authority.cvr === '') return true;

      if (authorities.value?.some(element => element.cvr.toString() === authority.cvr && element.id != authority.id))
        return false;
      return true;
    }

    function legitCvrValidator() {
      if (authority.cvr === '') return true;

      const isValidCvr = /^[0-9]{8}$/.test(authority.cvr);
      return isValidCvr;
    }

    watch(
      () => useResult(result).value,
      newValue => {
        if (newValue) Object.assign(authority, newValue);
        const selectedStatus = statusList.find(
          x => x.object === (newValue?.isActive ? FilterState.Active : FilterState.Inactive)
        );
        if (selectedStatus) status.value = selectedStatus;

        v$.value.$reset();
      },
      { immediate: true }
    );

    watch(
      () => [authority?.id, authority.name, authority.cvr, authority.isActive],
      () => {
        if (loading.value) return;
        if (hasChanges.value == null) {
          hasChanges.value = false;
        } else {
          hasChanges.value = true;
        }
      },
      { deep: true }
    );

    watch(
      () => status,
      newValue => {
        authority.isActive = newValue.value?.object === FilterState.Active;
      },
      { deep: true }
    );

    const createMutation = useCreateAuthorityMutation(authority);
    createMutation.onDone(response => {
      root.$emit('refetchAuthorities', response.data?.createAuthority);
      root.$router.back();
    });

    const updateMutation = useUpdateAuthorityMutation(authority);
    updateMutation.onDone(() => {
      root.$emit('refetchAuthorities');
      root.$router.back();
    });

    const deleteMutation = useDeleteAuthorityMutation(authority);
    deleteMutation.onDone(() => {
      root.$emit('refetchAuthorities');
      root.$router.back();
    });

    return {
      isNew,
      v$,
      authority,
      statusList,
      status,
      loading: computed(
        () =>
          loading.value ||
          createMutation.loading.value ||
          updateMutation.loading.value ||
          deleteMutation.loading.value
      ),
      primaryButton: isNew.value
        ? {
            ...useSaveButton(async () => {
              if (await v$.value.$validate()) createMutation.call();
            })
          }
        : {
            ...useSaveButton(async () => {
              if (await v$.value.$validate()) updateMutation.call();
            }),
            i18n: 'common.update'
          },
      secondaryButton: useCancelButton(() => root.$router.back()),
      menuItems: computed<IMenu[]>(() => [
        {
          name: root.$tc('common.delete'),
          icon: 'trash-alt',
          requireConfirmation: true,
          confirmationTitle: 'Slet myndighed',
          confirmationMessage: 'Vil du slette denne myndighed?',
          disabled: () => isNew.value,
          command: () => deleteMutation.call()
        }
      ])
    };
  }
});

function useValidations(
  uniqueNameValidator: () => boolean,
  uniqueCvrValidator: () => boolean,
  legitCvrValidator: () => boolean
) {
  return {
    name: {
      required,
      maxLength: maxLength(255),
      uniqueName: withI18nMessage(uniqueNameValidator)
    },
    cvr: {
      required,
      legitCvr: withI18nMessage(legitCvrValidator),
      uniqueCvr: withI18nMessage(uniqueCvrValidator)
    },
    isActive: {
      required
    }
  };
}
